<div class="account-switcher relative" [class.open]="isOpen()">
  <!-- ── Trigger button ───────────────────────────────────────────────────── -->
  <button
    type="button"
    class="account-switcher-trigger flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
    (click)="toggle($event)"
    [attr.aria-expanded]="isOpen()"
    [attr.aria-controls]="isOpen() ? 'account-switcher-listbox' : null"
    aria-haspopup="listbox"
    aria-label="Changer de compte"
  >
    <!-- Active account avatar -->
    @if (accountStore.activeAccount(); as account) {
      @if (avatarLogo(account); as logo) {
        <img
          [src]="logo"
          alt=""
          aria-hidden="true"
          (error)="onLogoError(account)"
          class="shrink-0 rounded-md bg-white object-cover"
          style="width: 32px; height: 32px"
        />
      } @else {
        <div [class]="getAvatarClass(account)" style="width: 32px; height: 32px" aria-hidden="true">
          {{ initials(account) }}
        </div>
      }
      <span class="hidden max-w-40 truncate text-[13px] font-semibold text-gray-900 sm:block">
        {{ account.name }}
      </span>
    } @else {
      <!-- Skeleton while accounts are loading -->
      <div class="h-8 w-8 animate-pulse rounded-full bg-gray-200"></div>
      <div class="hidden h-4 w-28 animate-pulse rounded bg-gray-200 sm:block"></div>
    }

    <i
      class="ri-arrow-down-s-line text-base text-gray-500 transition-transform duration-200"
      [class.rotate-180]="isOpen()"
      aria-hidden="true"
    ></i>
  </button>

  <!-- ── Dropdown panel ───────────────────────────────────────────────────── -->
  @if (isOpen()) {
    <div
      class="account-switcher-panel absolute left-0 z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg"
    >
      <!-- Search input -->
      <div class="border-b border-gray-100 p-2">
        <div class="relative">
          <i
            class="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"
            aria-hidden="true"
          ></i>
          <input
            type="text"
            class="w-full rounded-md border border-gray-200 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-700 placeholder-gray-400 focus:border-[#d4d4d4] focus:bg-white focus:outline-none"
            placeholder="Rechercher un compte..."
            [value]="searchQuery()"
            (input)="searchQuery.set($any($event.target).value)"
            aria-label="Rechercher un compte"
            (click)="$event.stopPropagation()"
          />
        </div>
      </div>

      <!-- Account list -->
      <div
        id="account-switcher-listbox"
        class="account-switcher-list max-h-64 overflow-y-auto py-1"
        role="listbox"
        aria-label="Sélectionner un compte"
      >
        @if (filteredAccounts().length === 0) {
          <p class="px-4 py-3 text-center text-sm text-gray-400">Aucun compte trouvé</p>
        }

        @for (account of filteredAccounts(); track account.accountId) {
          <div
            class="account-switcher-item mx-1 flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50"
            [style.background-color]="isActiveAccount(account) ? '#e8f5ec' : null"
            (click)="selectAccount(account)"
            role="option"
            [attr.aria-selected]="isActiveAccount(account)"
          >
            <!-- Avatar (merchant logo with coloured fallback) -->
            @if (avatarLogo(account); as logo) {
              <img
                [src]="logo"
                alt=""
                aria-hidden="true"
                (error)="onLogoError(account)"
                class="shrink-0 rounded-md bg-white object-cover"
                style="width: 32px; height: 32px"
              />
            } @else {
              <div
                [class]="getAvatarClass(account)"
                style="width: 32px; height: 32px"
                aria-hidden="true"
              >
                {{ initials(account) }}
              </div>
            }

            <!-- Account info -->
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="truncate text-[13px] font-bold text-gray-900">{{ account.name }}</span>
            </div>

            <!-- Active checkmark -->
            @if (isActiveAccount(account)) {
              <i class="ri-check-line shrink-0 text-base text-primary-600" aria-hidden="true"></i>
            }
          </div>
        }
      </div>
    </div>
  }

  <!-- ── Switch confirmation dialog (password re-auth) — @gozem/ui modal ───── -->
  <gz-modal-shell
    title="Changer de compte"
    [open]="!!pendingAccount()"
    size="sm"
    [showClose]="!submitting()"
    [closeOnBackdrop]="!submitting()"
    [scrollBody]="false"
    (close)="cancelSwitch()"
  >
    @if (pendingAccount(); as account) {
      <div class="switch-body">
        <!-- Target account -->
        <div class="switch-account">
          @if (avatarLogo(account); as logo) {
            <img
              [src]="logo"
              alt=""
              aria-hidden="true"
              (error)="onLogoError(account)"
              class="shrink-0 rounded-md bg-white object-cover"
              style="width: 40px; height: 40px"
            />
          } @else {
            <div
              [class]="getAvatarClass(account)"
              style="width: 40px; height: 40px"
              aria-hidden="true"
            >
              {{ initials(account) }}
            </div>
          }
          <span class="switch-account__name">{{ account.name }}</span>
        </div>

        <p class="switch-lead">Saisissez votre mot de passe pour vous connecter à ce compte.</p>

        <!-- Password -->
        <gz-form-field [invalid]="!!dialogError()" [error]="dialogError() ?? ''">
          <div class="switch-pw" (keydown.enter)="confirmSwitch()">
            <gz-input
              [type]="showPassword() ? 'text' : 'password'"
              iconLeft="ri-lock-2-line"
              placeholder="Mot de passe"
              [ngModel]="password()"
              (ngModelChange)="password.set($event)"
              [invalid]="!!dialogError()"
              ariaLabel="Mot de passe"
            />
            <button
              type="button"
              class="switch-pw-toggle"
              [attr.aria-label]="
                showPassword() ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
              "
              [attr.aria-pressed]="showPassword()"
              (click)="togglePassword()"
            >
              <i
                [class]="showPassword() ? 'ri-eye-off-line' : 'ri-eye-line'"
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </gz-form-field>
      </div>
    }

    <!-- Footer -->
    <div slot="footer" class="switch-footer">
      <gz-button variant="secondary" [disabled]="submitting()" (pressed)="cancelSwitch()">
        Annuler
      </gz-button>
      <gz-button
        variant="primary"
        [loading]="submitting()"
        [disabled]="!canConfirm()"
        (pressed)="confirmSwitch()"
      >
        Se connecter
      </gz-button>
    </div>
  </gz-modal-shell>
</div>
